/* Functional inquiry enhancement. No form data is sent to analytics or added to URLs. */
(() => {
  'use strict';
  const CAMPAIGN_KEY = 'lber.campaign.v1';
  const DRAFT_KEY = 'lber.inquiry-draft.v1';
  const attempts = new WeakMap();
  const campaignNames = ['UTM source', 'UTM medium', 'UTM campaign', 'Original landing page', 'Referral page'];
  const forms = () => Array.from(document.querySelectorAll('form[data-rental-inquiry]'));
  const get = (form, name) => form.elements.namedItem(`contact[${name}]`);
  const set = (form, name, value) => { const field = get(form, name); if (field) field.value = value || ''; };
  const storage = {
    read(key) { try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch (_) { return null; } },
    write(key, value) { try { sessionStorage.setItem(key, JSON.stringify(value)); } catch (_) { /* optional storage */ } },
    remove(key) { try { sessionStorage.removeItem(key); } catch (_) { /* optional storage */ } }
  };
  function requestID() {
    if (window.crypto && crypto.randomUUID) return `LBER-${crypto.randomUUID()}`;
    if (window.crypto && crypto.getRandomValues) {
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      return 'LBER-' + Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    }
    return `LBER-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 13)}`;
  }
  const cleanCampaign = value => (value || '').replace(/[^a-zA-Z0-9 _.-]/g, '').slice(0, 100);
  function safePath(value) {
    if (!value) return '';
    try { const url = new URL(value, location.origin); return url.origin + url.pathname; } catch (_) { return ''; }
  }
  function campaignAllowed() {
    try {
      const privacy = window.Shopify && Shopify.customerPrivacy;
      return !!privacy && privacy.analyticsProcessingAllowed() === true && privacy.marketingAllowed() === true;
    } catch (_) { return false; }
  }
  function updateCampaign() {
    if (!campaignAllowed()) {
      storage.remove(CAMPAIGN_KEY);
      forms().forEach(form => campaignNames.forEach(name => set(form, name, '')));
      return;
    }
    let campaign = storage.read(CAMPAIGN_KEY);
    if (!campaign || !campaign.createdAt || Date.now() - campaign.createdAt > 86400000) {
      const query = new URLSearchParams(location.search);
      campaign = {
        createdAt: Date.now(),
        'UTM source': cleanCampaign(query.get('utm_source')),
        'UTM medium': cleanCampaign(query.get('utm_medium')),
        'UTM campaign': cleanCampaign(query.get('utm_campaign')),
        'Original landing page': safePath(location.href),
        'Referral page': safePath(document.referrer)
      };
      storage.write(CAMPAIGN_KEY, campaign);
    }
    forms().forEach(form => campaignNames.forEach(name => set(form, name, campaign[name])));
  }
  function saveDraft(form) {
    const values = {};
    Array.from(form.elements).forEach(field => {
      if (!field.disabled && field.name && field.name.startsWith('contact[') && !campaignNames.some(name => field.name === `contact[${name}]`)) values[field.name] = field.value;
    });
    // Essential, tab-scoped recovery for a failed native submission; expires after 20 minutes.
    storage.write(DRAFT_KEY, {at: Date.now(), context: form.dataset.productContext || 'general', values});
  }
  function recoverDraft(form) {
    const draft = storage.read(DRAFT_KEY);
    if (!draft) return;
    if (Date.now() - draft.at > 1200000) { storage.remove(DRAFT_KEY); return; }
    if (!form.querySelector('[data-server-error]') || draft.context !== (form.dataset.productContext || 'general')) return;
    Object.entries(draft.values).forEach(([name, value]) => { const field = form.elements.namedItem(name); if (field) field.value = value; });
  }
  function dateValue(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const date = new Date(`${value}T12:00:00Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value ? date : null;
  }
  function validate(form, reveal = true) {
    let first = null;
    Array.from(form.querySelectorAll('[data-field]')).forEach(wrapper => {
      const field = wrapper.querySelector('input,textarea,select');
      const error = wrapper.querySelector('.field-error');
      if (!field || !error) return;
      field.setCustomValidity('');
      let message = '';
      if (field.required && !field.value.trim()) message = 'Please complete this field.';
      else if (field.validity.typeMismatch) message = 'Enter a valid email address.';
      else if (field.validity.patternMismatch) message = 'Enter a 5-digit ZIP or ZIP+4.';
      else if (field.validity.rangeUnderflow || field.validity.rangeOverflow || field.validity.stepMismatch || field.validity.badInput) message = 'Enter a whole quantity from 1 to 999.';
      const start = get(form, 'Requested start');
      const end = get(form, 'End date or duration');
      if (!message && field === start && !/^not sure yet$/i.test(field.value.trim()) && !dateValue(field.value.trim())) message = 'Use YYYY-MM-DD or choose Not sure yet.';
      if (!message && field === end && /^\d{4}-\d{2}-\d{2}$/.test(field.value.trim()) && !dateValue(field.value.trim())) message = 'Enter a valid end date or an expected duration.';
      if (!message && field === end && dateValue(start.value.trim()) && dateValue(field.value.trim()) && dateValue(field.value.trim()) < dateValue(start.value.trim())) message = 'The end date must be on or after the start date.';
      field.setCustomValidity(message);
      if (reveal || field.getAttribute('aria-invalid') === 'true') {
        error.textContent = message;
        field.setAttribute('aria-invalid', message ? 'true' : 'false');
      }
      if (message && !first) first = field;
    });
    const summary = form.querySelector('[data-validation-summary]');
    if (summary && (reveal || !first)) { summary.hidden = !first; summary.textContent = first ? 'Please check the marked fields. Your request has not been submitted.' : ''; }
    if (first && reveal) first.focus();
    return !first;
  }
  function prepareAttempt(form, renew = false) {
    const signature = JSON.stringify(Array.from(form.elements)
      .filter(field => !field.disabled && field.name?.startsWith('contact[') &&
        !['Request ID', 'Submission timestamp', 'Form render timestamp', ...campaignNames].some(name => field.name === `contact[${name}]`))
      .map(field => [field.name, field.value]));
    const previous = attempts.get(form);
    if (!renew && previous) {
      if (previous.signature !== signature) {
        previous.signature = signature;
        updateCampaign();
        saveDraft(form);
      }
      return previous;
    }
    set(form, 'Request ID', requestID());
    set(form, 'Submission timestamp', new Date().toISOString());
    updateCampaign();
    saveDraft(form);
    const attempt = {signature, submitted: false, keyboard: false};
    attempts.set(form, attempt);
    return attempt;
  }
  function guardActivation(event) {
    let form = null;
    if (event.type === 'submit') form = event.target;
    else {
      const control = event.target.closest?.('button,input');
      if (!control) return;
      if (event.type === 'click' && control.type !== 'submit') return;
      if (event.type === 'keydown' && (event.key !== 'Enter' || event.isComposing ||
          (control.tagName !== 'INPUT' && control.type !== 'submit'))) return;
      form = control.form;
    }
    if (!form?.matches('form[data-rental-inquiry]')) return;
    if (!validate(form)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    if (form.dataset.preview !== 'true') {
      const keyboardClick = event.type === 'click' && event.detail === 0 && attempts.get(form)?.keyboard;
      const attempt = prepareAttempt(form, event.type !== 'submit' && !keyboardClick);
      attempt.keyboard = event.type === 'keydown';
    }
  }
  function syncPressureTestFields(form) {
    const group = form.querySelector('[data-pressure-test-fields]');
    if (!group) return;
    const active = (get(form, 'Item SKU')?.value || form.dataset.productContext) === 'LBER-PT-001';
    group.hidden = !active;
    group.querySelectorAll('input').forEach(field => { field.disabled = !active; });
  }
  function initForm(form) {
    if (form.dataset.enhanced) return;
    form.dataset.enhanced = 'true';
    form.noValidate = false;
    recoverDraft(form);
    set(form, 'Request ID', requestID());
    set(form, 'Submission timestamp', new Date().toISOString());
    form.querySelectorAll('[data-unsure]').forEach(button => button.addEventListener('click', () => { set(form, 'Requested start', 'Not sure yet'); validate(form, false); get(form, 'Requested start').focus(); }));
    const itemChoice = form.querySelector('[data-item-choice]');
    if (itemChoice) itemChoice.addEventListener('change', () => {
      const option = itemChoice.selectedOptions[0];
      set(form, 'Item SKU', option.dataset.sku || 'GENERAL');
      set(form, 'Product ID', option.dataset.id || '');
      set(form, 'Product URL', option.dataset.url ? new URL(option.dataset.url, location.origin).href : '');
      syncPressureTestFields(form);
    });
    syncPressureTestFields(form);
    form.addEventListener('input', () => validate(form, false));
    form.addEventListener('change', () => validate(form, false));
    const nativeSubmit = form.submit.bind(form);
    form.submit = () => {
      if (attempts.get(form)?.submitted) return;
      if (!validate(form) || form.dataset.preview === 'true') return;
      const attempt = prepareAttempt(form);
      if (attempt.submitted) return;
      attempt.submitted = true;
      nativeSubmit();
    };
    form.addEventListener('submit', event => {
      if (!validate(form)) { event.preventDefault(); return; }
      if (form.dataset.preview !== 'true') prepareAttempt(form);
      if (form.dataset.preview === 'true') {
        event.preventDefault();
        const status = form.querySelector('[data-preview-status]');
        status.hidden = false;
        status.textContent = 'The fields passed this preview check. This local preview cannot submit an inquiry. No request was sent and inbox delivery remains untested.';
        status.focus();
        return;
      }
      // Native Shopify POST handles spam checks, delivery and server-confirmed success.
    });
  }
  function showConfirmedDetails() {
    const success = document.querySelector('[data-submission-success]');
    if (!success) return;
    const summary = success.querySelector('[data-success-summary]');
    const draft = storage.read(DRAFT_KEY);
    if (summary && draft && Date.now() - draft.at < 1200000) {
      const values = draft.values || {};
      const detail = name => values[`contact[${name}]`] || '';
      const rows = [
        ['Request reference', detail('Request ID')],
        ['Item', detail('Equipment item')],
        ['Project location', [detail('Project city'), detail('Project ZIP')].filter(Boolean).join(', ')],
        ['Requested dates', [detail('Requested start'), detail('End date or duration')].filter(Boolean).join(' to ')]
      ];
      rows.forEach(([label, value]) => { if (value) { const row = document.createElement('span'); row.textContent = `${label}: ${value}`; summary.appendChild(row); } });
    }
    storage.remove(DRAFT_KEY);
    success.focus();
  }
  function init() {
    showConfirmedDetails();
    forms().forEach(initForm);
    updateCampaign();
    if (window.Shopify && typeof Shopify.loadFeatures === 'function') Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.1'}], error => { if (!error) updateCampaign(); });
  }
  document.addEventListener('click', guardActivation, true);
  document.addEventListener('keydown', guardActivation, true);
  document.addEventListener('submit', guardActivation, true);
  document.addEventListener('keyup', event => {
    if (event.key !== 'Enter') return;
    const attempt = event.target.form && attempts.get(event.target.form);
    if (attempt) attempt.keyboard = false;
  }, true);
  document.addEventListener('visitorConsentCollected', updateCampaign);
  document.addEventListener('shopify:section:load', init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
